import { useNavigate, useParams } from "react-router";
import { Trash2 } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import { eftHashPath } from "@cle/constants/routes";
import ConfirmationModal from "@cle/components/ConfirmationModal";
import RoundedCard from "@cle/components/RoundedCard";
import {
  useDeleteLegalAid,
  useLegalAidDocument,
  useLegalAidDocumentFile,
} from "@/hooks/useLegalAid";
import { formatDanishLongDate } from "@/util/date";
import ManualReviewInfoModal from "./components/ManualReviewInfoModal";
import SecretariatMessageCard from "./components/SecretariatMessageCard";
import UploadedDocumentPreviewCard from "./components/UploadedDocumentPreviewCard";
import { formatLessons } from "../../utils/lessonLimits";

const MISSING_VALUE = "-";

const LegalAidDocumentView = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: legalAid,
    isError: isLegalAidError,
    isLoading: isLegalAidLoading,
  } = useLegalAidDocument(id);
  const {
    data: documentFile,
    isError: isDocumentFileError,
    isLoading: isDocumentFileLoading,
  } = useLegalAidDocumentFile(id);
  const deleteLegalAidMutation = useDeleteLegalAid();
  const legalAidTitle = legalAid?.fileName ?? "Advokatvagt-retshjælp.pdf";
  const isReported = legalAid?.isReported ?? false;
  const isDeleteDisabled =
    id.length === 0 || deleteLegalAidMutation.isPending || isReported;

  const handleDeleteLegalAid = (closeConfirmation: () => void) => {
    deleteLegalAidMutation.mutate(id, {
      onSuccess: () => {
        closeConfirmation();
        void navigate(eftHashPath("legal-aid"));
      },
    });
  };

  return (
    <div className="cle-course-certificate-document">
      {isLegalAidError ? (
        <div className="alert alert-warning" role="alert">
          Dokumentationens oplysninger kunne ikke indlæses lige nu.
        </div>
      ) : null}
      {deleteLegalAidMutation.isError ? (
        <div className="alert alert-warning" role="alert">
          Dokumentationen kunne ikke slettes lige nu. Prøv igen.
        </div>
      ) : null}

      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-6">
          <UploadedDocumentPreviewCard
            certificateTitle={legalAidTitle}
            fileBlob={documentFile}
            isError={isDocumentFileError}
            isLoading={isDocumentFileLoading}
          />
        </div>
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <RoundedCard
              className="cle-course-certificate-document__side-card"
              header="Information"
              action={
                <>
                  <ManualReviewInfoModal
                    documentType="advokatvagt/retshjælp"
                    isReported={isReported}
                  />
                  <ConfirmationModal
                    title="Slet dokumentation"
                    description="Er du sikker på, at du vil slette dokumentationen? Handlingen kan ikke fortrydes."
                    confirmLabel="Slet"
                    confirmVariant="destructive"
                    confirmButtonId="cle-confirm-delete-legal-aid"
                    cancelButtonId="cle-cancel-delete-legal-aid"
                    isConfirming={deleteLegalAidMutation.isPending}
                    isConfirmDisabled={isDeleteDisabled}
                    onConfirm={({ close }) => handleDeleteLegalAid(close)}
                    trigger={
                      <CleButton
                        id="cle-delete-legal-aid"
                        variant="destructive"
                        size="sm"
                        disabled={isDeleteDisabled}
                      >
                        <Trash2 size={16} aria-hidden strokeWidth={1.75} />
                        {deleteLegalAidMutation.isPending
                          ? "Sletter..."
                          : "Slet"}
                      </CleButton>
                    }
                  />
                </>
              }
            >
              <dl className="cle-course-certificate-document__info-list mb-0">
                <dt>Dokument</dt>
                <dd>{legalAidTitle}</dd>

                <dt>Antal lektioner</dt>
                <dd>
                  {isLegalAidLoading
                    ? "Indlæser"
                    : formatLessons(legalAid?.numberOfLessons, MISSING_VALUE)}
                </dd>

                <dt>Uploadet</dt>
                <dd>
                  {formatDanishLongDate(legalAid?.createdAt, MISSING_VALUE)}
                </dd>
              </dl>
            </RoundedCard>
            <SecretariatMessageCard subjectId={id} isReported={isReported} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalAidDocumentView;
